import { Box, Button, Link, Stack, Typography } from '@mui/material';

import { AccountError } from '../../graphql/generated/graphql';

const SUPPORT_EMAIL = 'support@getaltitude.ai';

// Reason-specific copy. Kept user-facing and blame-free — the underlying causes (wrong WorkOS
// org, missing profile) are not something the end user did wrong or can resolve themselves.
const MESSAGE_BY_ERROR: Record<AccountError, string> = {
  [AccountError.OrganizationNotProvisioned]:
    "Your sign-in isn't linked to a company account yet. This usually happens when an invitation was set up against a different organisation.",
  [AccountError.ProfileIncomplete]:
    "Your account is missing some required details, so we couldn't open it automatically.",
};

/**
 * Terminal "contact support" screen for an authenticated WorkOS session that can't be mapped
 * to a usable Altitude account. Rendered by `ViewerProvider` in place of the app, so there is
 * no route to navigate past it: the underlying state is one the user can't fix themselves, and
 * letting them continue into self-serve onboarding would spin up a duplicate company. Offers a
 * sign-out so they can retry with a different account.
 */
export const AccountErrorPage = ({ error, onSignOut }: { error: AccountError; onSignOut: () => void }) => (
  <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
    <Stack spacing={3} sx={{ maxWidth: 460, textAlign: 'center' }}>
      <Typography variant="h4">We couldn&apos;t open your account</Typography>
      <Typography color="text.secondary">{MESSAGE_BY_ERROR[error]}</Typography>
      <Typography color="text.secondary">
        Please contact <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link> and we&apos;ll get it sorted out.
      </Typography>
      <Box>
        <Button variant="outlined" onClick={onSignOut}>
          Sign out
        </Button>
      </Box>
    </Stack>
  </Box>
);

export default AccountErrorPage;
